/**
 * Monaco 0.44's `DefaultLinesDiffComputer` has latent invariant violations in
 * its diff post-processing (heuristicSequenceOptimizations + range validation).
 * When an invariant fails, Monaco calls
 *
 *   onUnexpectedError(new BugIndicatingError('Assertion Failed'))
 *
 * which the default `errorHandler.unexpectedErrorHandler` schedules as
 * `setTimeout(() => { throw new Error(e.message + '\n\n' + e.stack); }, 0)`.
 * The diff itself still returns a valid result (assertFn does not throw), so
 * the user sees nothing broken — but the async throw lands on
 * `window.addEventListener('error')` and our crash dialog treats it as a real
 * renderer crash.
 *
 * We can't patch Monaco's algorithm without forking `node_modules`, so we
 * suppress this specific error class at the global error listener, mirroring
 * the existing ResizeObserver-loop suppression.
 *
 * The rethrow produces an Error with:
 *   - `message` starting with `"Assertion Failed"`
 *   - `stack` containing the original BugIndicatingError construction frames
 *     AND a final frame inside Monaco's `errors.js` setTimeout callback
 *
 * In the production AMD build the stack references the bundled file via
 * `#editorWorkerService` URL fragments (see the reported crash signature). In
 * dev/tests we see the unbundled ESM path. The filter accepts either.
 */

const MONACO_STACK_MARKERS = [
  'monaco-editor',
  'monaco/vs',
  'editorWorkerService',
  'editor.main.js',
] as const;

export function isMonacoBugIndicatingError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = typeof error.message === 'string' ? error.message : '';
  if (!message.startsWith('Assertion Failed')) return false;
  const stack = typeof error.stack === 'string' ? error.stack : '';
  const haystack = `${stack}\n${message}`;
  return MONACO_STACK_MARKERS.some((m) => haystack.includes(m));
}

/**
 * Benign async cancellations that surface as unhandled rejections:
 *
 * - `@monaco-editor/loader`'s `makeCancelable` REJECTS its wrapped init promise
 *   with the plain object `{type: 'cancelation', msg: 'operation is manually
 *   canceled'}` (their spelling) whenever an `<Editor>` unmounts before Monaco
 *   finishes initializing. Session restore mounts several editors at once and
 *   any quick unmount (tab switch, remount) triggers it — by design, nothing
 *   is broken.
 * - Monaco's own cancellation (`vs/base/common/errors` `CancellationError`) is
 *   an Error named `Canceled` with message `Canceled`; VS Code's global
 *   unexpected-error handler ignores it the same way.
 */
export function isBenignCancellation(error: unknown): boolean {
  if (error instanceof Error) {
    return error.name === 'Canceled' && error.message === 'Canceled';
  }
  if (typeof error === 'object' && error !== null) {
    return (error as { type?: unknown }).type === 'cancelation';
  }
  return false;
}
